import type { ModalConfig } from 'src/types/Modals';
import { modalStore } from 'src/stores/ui/modal_states';

export class ModalManager {
	private modalsQueue: ModalConfig[] = [];

	constructor(modals: ModalConfig[]) {
		this.modalsQueue = this.sortByPriority(modals);
	}

	private sortByPriority(modals: ModalConfig[]): ModalConfig[] {
		return [...modals].sort((a, b) => (b.props.priority || 0) - (a.props.priority || 0));
	}

	evaluateModal(modal: ModalConfig): boolean {
		if (!modal.rules?.length) return false;
		return modal.rules.every((rule) => rule.check() === rule.expected);
	}

	getActiveModals(): ModalConfig[] {
		return this.modalsQueue.filter((modal) => this.evaluateModal(modal)).slice(0, 2);
	}

	setModals(modals: ModalConfig[]) {
		this.modalsQueue = this.sortByPriority(modals);
		return this.getActiveModals();
	}

	addModal(modal: ModalConfig) {
		const modalExists = this.modalsQueue.some((m) => m.id === modal.id);

		if (modalExists) {
			// If modal already exists, we can update it or ignore
			this.modalsQueue = this.modalsQueue
				.map((m) => (m.id === modal.id ? modal : m))
				.filter((modal) => this.evaluateModal(modal))
				.slice(0, 2);
			return this.modalsQueue;
		}

		this.modalsQueue = [...this.modalsQueue, modal]
			.filter((modal) => this.evaluateModal(modal))
			.slice(0, 2);
		return this.modalsQueue;
	}

	showModal(modal: ModalConfig) {
		this.modalsQueue = [modal].filter((modal) => this.evaluateModal(modal));
		return this.modalsQueue;
	}

	hideModal(modalId: string) {
		this.modalsQueue = this.modalsQueue.filter(
			(modal) => modal.id !== modalId && this.evaluateModal(modal),
		);
		return this.modalsQueue;
	}
}

export const initializeModalManager = async () => {
	if (modalStore.isInitialized()) {
		return;
	}

	modalStore.setIsInitialized(true);
	const { modalsConfig } = await import('src/configs/modals');
	modalStore.initManager(await modalsConfig());
};
